import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { MatCardModule } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSortModule, Sort } from '@angular/material/sort';
import { BehaviorSubject, filter, take } from 'rxjs';
import moment from 'moment';
import { UserContextProvider } from '../../../infrastructure/context/user-context-provider';
import { Logger } from '../../../infrastructure/logging/logger';
import { DataTableColumn } from '../../../shared/components/data-table/DataTableColumn.model';
import { formatCurrencyString } from '../../../utilities/CurrencyUtility';
import { dateFormat } from '../../../utilities/DateUtility';
import { claimStatusIdToStatus, getClaimStatusColor, getClaimStatusIdFromFormattedStatus } from '../ClaimHelper';
import { ClaimFilterStatusComponent } from '../components/filters/claim-filter-status/claim-filter-status.component';
import { ClaimFilterStateService } from '../services/claim-filter-state.service';
import { ClaimMessageService } from '../services/claim-message.service';
import { ClaimsService } from '../services/claims.service';
import { DeleteUndeleteClaimDialogComponent } from '../claim-detail/components/delete-undelete-claim-dialog/delete-undelete-claim-dialog.component';
import { NavService } from '../../../layout/nav/nav.service';
import { DataTableFilter } from '../../../shared/components/data-table/filters/components/DataTableFilter';
import { SearchService } from '../../../shared/search/search.service';
import { SortParams, FilterParams } from '../../../shared/search/SearchParams';
import { ClaimRow } from '../models/Claim';
import { DataTableComponent } from '../../../shared/components/data-table/data-table.component';
import { DeletedClaimsTableComponent } from '../components/deleted-claims-table/deleted-claims-table.component';

@Component({
  selector: 'app-claims',
  templateUrl: './claims.component.html',
  styleUrls: ['./claims.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIcon, 
    MatCardModule, 
    MatSortModule,
    MatTabsModule,
    DataTableComponent,
    DeletedClaimsTableComponent
  ]
})
export class ClaimsComponent implements OnInit {

  @ViewChild(MatTabGroup) matTabGroup!: MatTabGroup;

  filterDefinitions: DataTableFilter[] = [
    { id: 'claim-fitler-status', name: 'Claim Status', shortName: 'Status', component: ClaimFilterStatusComponent }
  ];

  tableColumns: DataTableColumn[] = [
    { column: 'id', display: 'Claim ID' },
    { column: 'organizationId', display: 'Organization ID' },
    { column: 'organizationName', display: 'Organization Name' },
    { column: 'reservationId', display: 'Reservation ID' },
    { column: 'propertyName', display: 'Property Name' },
    { column: 'firstName', display: 'First Name' },
    { column: 'lastName', display: 'Last Name' },
    { column: 'reservationDates', display: 'Reservation Dates' },
    { column: 'estimateAmount', display: 'Estimated Claim Amount' },
    { column: 'actualAmount', display: 'Amount Paid' },
    { column: 'pillStatus', display: 'Status' }
  ];

  deleteClaimTableColumns: DataTableColumn[] = [
    { column: 'id', display: 'Claim ID' },
    { column: 'organizationId', display: 'Organization ID' },
    { column: 'organizationName', display: 'Organization Name' },
    { column: 'reservationId', display: 'Reservation ID' },
    { column: 'propertyName', display: 'Property Name' },
    { column: 'firstName', display: 'First Name' },
    { column: 'lastName', display: 'Last Name' },
    { column: 'reservationDates', display: 'Reservation Dates' },
    { column: 'estimateAmount', display: 'Estimated Claim Amount' },
    { column: 'actualAmount', display: 'Amount Paid' },
    { column: 'pillStatus', display: 'Status' },
    { column: 'action', display: 'Action' }
  ];

  tableToApiColumnMap: { [key: string]: string } = {
    'id': 'claimId',
    'organizationId': 'organizationId',
    'organizationName': 'organizationName',
    'reservationId': 'reservationId',
    'propertyName': 'propertyName',
    'firstName': 'firstName',
    'lastName': 'lastName',
    'reservationDates': 'arrivalDate',
    'estimateAmount': 'estimateAmount',
    'actualAmount': 'actualAmount',
    'pillStatus': 'status'
  }

  apiToTableColumnMap: { [key: string]: string } = {
    'claimId': 'id',
    'organizationId': 'organizationId',
    'organizationName': 'organizationName',
    'reservationId': 'reservationId',
    'propertyName': 'propertyName',
    'firstName': 'firstName',
    'lastName': 'lastName',
    'arrivalDate': 'reservationDates',
    'estimateAmount': 'estimateAmount',
    'actualAmount': 'actualAmount',
    'status': 'pillStatus'
  }

  loading = new BehaviorSubject<boolean>(false);
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  activeClaimsDataSource = new MatTableDataSource<ClaimRow>();
  deletedClaimsDataSource = new MatTableDataSource<any>();
  mappedDeletedClaims: any[] = [];
  pageSize = 10;
  pageNumber!: number;
  activeClaimsTableLength = 0;
  deletedClaimsTableLength = 0;
  keyword = '';
  displayActiveClaims = true;

  initialPageSize = new BehaviorSubject<number>(10);
  initialPageNumber = new BehaviorSubject<number>(1);
  initialKeyword = new BehaviorSubject<string>('');
  initialSort = new BehaviorSubject<Sort | undefined>(undefined);

  selectedOrganization!: any;
  selectedOrganizationName: string = '';
  selectedOrganizationId: number = 0;

  constructor(
    private router: Router,
    private claimMessageService: ClaimMessageService,
    private claimService: ClaimsService,
    private filterState: ClaimFilterStateService,
    private searchService: SearchService,
    private userContextProvider: UserContextProvider,
    private logService: Logger,
    private navService: NavService,
    public dialog: MatDialog
  ) { }

  ngOnInit() {
    this.userContextProvider.isContextLoaded$.pipe(
      filter(loaded => loaded),
      take(1)
    ).subscribe(_ => {
      this.setSearchQuery();
      this.setupFilterEventHandler();
      this.setupPagingState();
      this.setupFilterState();
      this.setupSearchTextState();
      this.setupSortState();
    });

    this.navService.selectedOrg$.subscribe((org: any) => {
      if (org?.organization_id) {
        this.selectedOrganization = org;
        this.selectedOrganizationId = org.organization_id;
        this.onOrganizationSearchEvent(org.organization_id);
      } else {
        this.selectedOrganizationId = 0;
        this.selectedOrganization = undefined;
        this.selectedOrganizationName = '';
        this.loadPage();
      }
    });
  }

  setSearchQuery() {
    if (this.searchService.currentQuery.sortParams?.sortField === 'claimId') {
      if (this.searchService.currentQuery.page > 1) {
        this.searchService.setPage(this.searchService.currentQuery.page);
        this.pageNumber = this.searchService.currentQuery.page;
        this.initialPageNumber = new BehaviorSubject<number>(this.searchService.currentQuery.page);
      } else {
        this.searchService.setPage(1);
        this.pageNumber = 1;
      }
    } else {
      this.searchService.resetQuery();
    }
    this.searchService.setSort("claimId", -1);
  }

  private setupPagingState() {
    const pageSize = this.claimService.lastSearchRequest?.pageSize;
    if (pageSize) {
      this.initialPageSize.next(pageSize);
      this.searchService.setPageSize(pageSize);
      this.pageSize = pageSize;
    }
  }

  private setupFilterState() {
    if (this.claimService.lastSearchRequest?.filterParams?.find(f => f.fieldName === 'status')) {
      this.handleStatusFilterUpdate(this.filterState.statusFilter.draft, 'Draft');
      this.handleStatusFilterUpdate(this.filterState.statusFilter.submitted, 'In Progress');
      this.handleStatusFilterUpdate(this.filterState.statusFilter.completed, 'Completed');
    }
  }

  private setupSearchTextState() {
    if (this.claimService.lastSearchRequest?.freeText) {
      this.addKeywordToQuery(this.claimService.lastSearchRequest.freeText);
      this.initialKeyword.next(this.claimService.lastSearchRequest.freeText);
    }
  }

  private setupSortState() {
    const sort = this.claimService.lastSearchRequest?.sortParams;
    if (sort) {
      this.searchService.addSort(sort);
      const sortField = this.getSortPropertyNameForTable(sort.sortField);
      const sortDirection = sort.sortOrder == 1 ? 'asc' : 'desc';
      this.initialSort.next({ active: sortField, direction: sortDirection });
    } else {
      this.initialSort.next({ active: 'id', direction: 'desc' });
    }
  }

  setupFilterEventHandler() {
    this.claimMessageService.applyFilterEvent.subscribe(_ => {
      this.handleStatusFilterUpdate(this.filterState.statusFilter.draft, 'Draft');
      this.handleStatusFilterUpdate(this.filterState.statusFilter.submitted, 'In Progress');
      this.handleStatusFilterUpdate(this.filterState.statusFilter.completed, 'Completed');
      this.loadPage();
    });
  }

  async loadPage() {
    if (this.filterState.allStatusFiltersDisabled()) {
      this.updateActiveClaimsTableDataSource([], 0);
      return;
    }

    try {
      this.loading.next(true);

      await Promise.all([
        this.loadActiveClaims(),
        this.loadDeletedClaims()
      ]);
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Claims', 'An error occurred while retrieving Claims');
    } finally {
      this.loading.next(false);
    }
  }

  async loadActiveClaims() {
    try {
      this.activeClaimsDataSource.data = [];
      const { claims, totalRecords } = await this.claimService.searchClaims(this.searchService.currentQuery);
      const mappedClaims = claims.map((claim: any) => this.buildClaimRow(claim));
      this.updateActiveClaimsTableDataSource(mappedClaims, totalRecords);
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Claims', 'An error occurred while retrieving Claims');
    }
  }

  async loadDeletedClaims(pageNumber?: number) {
    this.deletedClaimsDataSource.data = [];
    let page: number = 1;
    if (pageNumber) {
      page = pageNumber;
    }

    const deletedClaimsResponse = await this.claimService.getClaims({
      filter: this.filterState.statusFilter,
      page: page,
      pageSize: 10,
      keyword: '',
      arrivalDate: undefined,
      departureDate: undefined,
      activeClaims: false,
      organizationId: this.selectedOrganizationId
    });
    let deletedClaims = deletedClaimsResponse.claims;
    this.deletedClaimsTableLength = deletedClaimsResponse.totalRecords;
    this.mapDeletedClaims(deletedClaims);
  }

  updateActiveClaimsTableDataSource(claims: ClaimRow[], totalRecords: number) {
    if (this.selectedOrganization) {
      this.selectedOrganizationName = this.selectedOrganization.organization_name;
    } else {
      this.selectedOrganizationName = '';
    }
    this.activeClaimsDataSource = new MatTableDataSource(claims);
    this.activeClaimsTableLength = totalRecords;
  }

  resetSearchQuery() {
    this.searchService.resetQuery();
    this.searchService.setSort('claimId', -1);
  }

  async onActiveClaimsPageEvent(event: PageEvent): Promise<void> {
    this.pageSize = event.pageSize
    this.searchService.setPageSize(event.pageSize);
    this.searchService.setPage(event.pageIndex + 1);
    try {
      this.loading.next(true);
      await this.loadActiveClaims();
    } finally {
      this.loading.next(false);
    }
  }

  onDeletedClaimsPageEvent(event: PageEvent): void {
    this.loadDeletedClaims(event.pageIndex + 1);
  }

  async onActiveClaimsSearchEvent(keyword: string): Promise<void> {
    this.resetSearchQuery();
    this.keyword = keyword;
    this.searchService.addTextQuery(keyword);
    this.searchService.setPage(1);
    try {
      this.loading.next(true);
      await this.loadActiveClaims();
    } finally {
      this.loading.next(false);
    }
  }

  onDeletedClaimsSearchEvent(keyword: string) {
    this.deletedClaimsDataSource.filter = keyword.trim().toLowerCase();
  }

  private addKeywordToQuery(keyword: string) {
    this.keyword = keyword;
    this.searchService.addTextQuery(keyword);
    this.searchService.setPage(1);
  }

  onOrganizationSearchEvent(organizationId: any) {
    this.searchService.setOrganizationId(organizationId);
    this.searchService.setPage(1);
    this.loadPage();
  }

  onActiveClaimsSortEvent(sortParams: SortParams) {
    this.addSortToQuery(sortParams);
    this.loadPage();
  }

  private addSortToQuery(sortParams: SortParams) {
    sortParams.sortField = this.tableToApiColumnMap[sortParams.sortField];
    this.searchService.addSort(sortParams);
  }

  onDeletedClaimsSortEvent(sort: any) {
    this.deletedClaimsDataSource.data = this.mappedDeletedClaims.sort((a, b) => {
      const isAsc = sort.sortOrder === 1;
      return this._compare(a[sort.sortField], b[sort.sortField], isAsc);
    });
  }

  private _compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  private getSortPropertyNameForTable(field: string) {
    return this.apiToTableColumnMap[field];
  }

  onNewClaimClick(): void {
    this.router.navigate(['/reservations']);
  }

  updateStatusFilter(statusValue: string) {
    this.searchService.updateFilter({
      fieldName: 'status',
      value: [statusValue],
      range: false
    });
  }

  removeStatusFilterValue(statusValue: string) {
    this.searchService.removeValueFromFilter('status', statusValue);
  }

  handleStatusFilterUpdate(statusEnabled: boolean, statusName: string) {
    if (statusEnabled) {
      this.updateStatusFilter(statusName);
    } else {
      this.removeStatusFilterValue(statusName);
    }
  }

  handleDateFilterUpdate(dateValue: string, filterName: string, rangeType: number) {
    if (dateValue) {
      this.addDateFilter(dateValue, filterName, rangeType);
    } else {
      this.removeDateFilter(filterName);
    }
  }

  addDateFilter(dateValue: string, filterName: string, rangeType: number) {
    if (!dateValue || !filterName) {
      return;
    }

    const formattedDate = formatDate(dateValue, 'yyyy-MM-dd', 'en-US', 'UTC');
    const filter: FilterParams = {
      fieldName: filterName,
      value: [formattedDate],
      range: true,
      rangeType: rangeType
    };

    this.searchService.replaceFilter(filter);
  }

  removeDateFilter(filterName: string) {
    this.searchService.removeFilter(filterName);
  }

  buildClaimRow(claim: any): ClaimRow {
    let reservationStart = '';
    let reservationEnd = '';

    try {
      reservationStart = dateFormat(claim.arrivalDate, this.userContextProvider.region);
      reservationEnd = dateFormat(claim.departureDate, this.userContextProvider.region);
    } catch (e) {
      this.logService.handleError(e, `Could not convert Reservation Start Date: ${claim.arrivalDate} or Reservation End Date: ${claim.departureDate}`, `Could not convert Reservation Start Date: ${claim.arrivalDate} or Reservation End Date: ${claim.departureDate}`);
    }

    let claimStatusText;
    switch(claim.status) {
      case 'In Progress':
        claimStatusText = `${claim.status} - ${moment().diff(moment(claim.submittedDate), 'days')} Days`;
        break;
      case 'Completed': 
        if (!claim.actualAmount) {
          claimStatusText = 'Denied'
        } else {
          claimStatusText = 'Completed - ' + dateFormat(claim.updatedDate, this.userContextProvider.region); 
        }
        break;
      case 'Draft':
        claimStatusText = claim.status;
        break;
      default: 
        claimStatusText = claim.status;
    }

    return {
      id: claim.claimId,
      referenceId: '',
      organizationId: claim.organizationId,
      organizationName: claim.organizationName,
      reservationId: claim.reservationId,
      propertyName: claim.propertyName,
      firstName: claim.firstName,
      lastName: claim.lastName,
      reservationDates: `${reservationStart} - ${reservationEnd}`,
      estimateAmount: formatCurrencyString(claim.estimateAmount, this.userContextProvider.currencyCode),
      actualAmount: formatCurrencyString(claim.actualAmount, this.userContextProvider.currencyCode),
      pillStatus: claimStatusText
    }
  }

  getPillStatusColor(status?: string): string {
    let statusText;
    if (status?.substring(0, 5) === 'Draft' || status?.substring(0, 11) === 'In Progress') {
      statusText = status?.substring(0, 11);
    } else if (status === 'Denied') {
      statusText = 'Denied';
    } else {
      statusText = 'Completed';
    }
    return getClaimStatusColor(getClaimStatusIdFromFormattedStatus(statusText));
  }

  async mapDeletedClaims(deletedClaims: any) {
    this.deletedClaimsDataSource.data = [];
    this.mappedDeletedClaims = deletedClaims.map((claim: any) => this.buildDeletedClaimRow(claim));
    this.updateDeletedTableDataSource(this.mappedDeletedClaims);
  }

  buildDeletedClaimRow(claim: any): ClaimRow {
    let guest_first_name = '';
    let guest_last_name = '';
    if (claim.first_guest) {
      guest_first_name = claim.first_guest.first_name;
      guest_last_name = claim.first_guest.last_name;
    } 
    
    let reservationStart = '';
    let reservationEnd = '';

    try {
      reservationStart = dateFormat(claim.reservation.arrival_date, this.userContextProvider.region);
      reservationEnd = dateFormat(claim.reservation.departure_date, this.userContextProvider.region);
    } catch (e) {
      this.logService.handleError(e, `Could not convert Reservation Start Date: ${claim.arrivalDate} or Reservation End Date: ${claim.departureDate}`, `Could not convert Reservation Start Date: ${claim.arrivalDate} or Reservation End Date: ${claim.departureDate}`);
    }

    return {
      id: claim.id,
      referenceId: '',
      organizationId: claim.organization_id,
      organizationName: claim.organization.name,
      reservationId: claim.reservation_id,
      propertyName: claim.property.name,
      firstName: guest_first_name,
      lastName: guest_last_name,
      reservationDates: `${reservationStart} - ${reservationEnd}`,
      estimateAmount: formatCurrencyString(claim.estimate_amount, this.userContextProvider.currencyCode),
      actualAmount: formatCurrencyString(claim.actual_amount, this.userContextProvider.currencyCode),
      pillStatus: claimStatusIdToStatus(claim.status_id),
      action: true
    }
  }

  updateDeletedTableDataSource(deletedClaims: ClaimRow[]) {
    if (this.selectedOrganization) {
      this.selectedOrganizationName = this.selectedOrganization.organization_name;
    } else {
      this.selectedOrganizationName = '';
    }
    this.deletedClaimsDataSource = new MatTableDataSource(deletedClaims);
  }

  onUndeleteClaimActionClick(row: any) {
    this.claimService.getClaimById(row.id).then(claim => {
      this.openUndeleteClaimDialog(row, claim);
    });
  }

  openUndeleteClaimDialog(row: any, claim: any) {
    const undeleteDialogRef = this.dialog.open(DeleteUndeleteClaimDialogComponent, {
      width: '750px',
      disableClose: true,
      data: {
        claim: claim,
        claimRow: row,
        deleteClaim: false
      }
    });
    undeleteDialogRef.afterClosed().subscribe((statusId: number) => {
      if (statusId > 0) {
        try {
          const undeleteClaimPayload = {
            id: claim.id.toString(),
            is_active: 1,
            status_id: 1
          }

          this.claimService.updateClaimStatus(claim.id, undeleteClaimPayload).then(_ => {
            this.loadPage();
            this.matTabGroup.selectedIndex = 0;
          });
        } catch (e) {
          this.logService.handleError(e, 'An error occurred while updating Claim status', 'An error occurred while updating Claim status');
        }
      }
    });
  }
}